import type { CSSProperties, MouseEventHandler } from "react";

import { cdnImageSrcSet, cdnImageUrl } from "@/lib/cdn-image";
import { cn } from "@/lib/utils";

interface ImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  fill?: boolean;
  /**
   * Loads eagerly and hints high fetch priority. Use for images already in
   * the viewport at first paint — a hero, the first row of a grid. Everything
   * else should stay lazy.
   */
  priority?: boolean;
  sizes?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLImageElement>;
}

/**
 * Every image on the site goes through here, which is what makes it the
 * right place to apply Cloudflare's edge transformations: sources stay as
 * plain R2 URLs everywhere else in the codebase, and resizing/format
 * negotiation is applied once, here. See `lib/cdn-image.ts`.
 */
export const Image = ({
  src,
  alt,
  width,
  height,
  className,
  fill,
  priority,
  sizes,
  style,
  ...props
}: ImageProps) => {
  const transformed = cdnImageUrl(src, { width });
  const srcSet = cdnImageSrcSet(src);

  // Without `sizes` the browser assumes 100vw and over-fetches from srcset.
  const defaultSizes = fill ? "100vw" : undefined;
  const resolvedSizes = sizes ?? (width ? `${width}px` : defaultSizes);

  const loading = priority ? ("eager" as const) : ("lazy" as const);
  const fetchPriority = priority ? ("high" as const) : ("auto" as const);

  if (fill) {
    return (
      // oxlint-disable-next-line next/no-img-element
      <img
        src={transformed}
        srcSet={srcSet}
        sizes={resolvedSizes}
        alt={alt}
        draggable={false}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        className={cn("absolute inset-0 h-full w-full", className)}
        style={style}
        {...props}
      />
    );
  }

  return (
    // oxlint-disable-next-line next/no-img-element
    <img
      src={transformed}
      srcSet={srcSet}
      sizes={resolvedSizes}
      alt={alt}
      draggable={false}
      width={width}
      height={height}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding="async"
      className={className}
      style={style}
      {...props}
    />
  );
};
